import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Instagram, Music, Youtube, ExternalLink, Facebook, Twitter, ChevronLeft, ChevronRight, Mic2,
} from "lucide-react";
import { api } from "@/lib/api";
import PageTransition, { Section } from "@/components/site/PageTransition";
import Seo from "@/components/site/Seo";
import VinylLoader from "@/components/site/VinylLoader";
import YouTubeHoverCard from "@/components/site/YouTubeHoverCard";

/* Simple horizontal-scroll carousel with arrow controls + snap */
function Carousel({ children, testid }) {
  const ref = useRef(null);
  const scroll = (dir) => {
    const node = ref.current;
    if (!node) return;
    const card = node.querySelector("[data-carousel-item]");
    const step = card ? card.getBoundingClientRect().width + 16 : 320;
    node.scrollBy({ left: dir * step, behavior: "smooth" });
  };
  return (
    <div className="relative" data-testid={testid}>
      <div
        ref={ref}
        className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth scrollbar-thin"
        style={{ scrollbarWidth: "thin" }}
      >
        {children}
      </div>
      <button
        onClick={() => scroll(-1)}
        className="hidden md:flex absolute -left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center bg-black/60 backdrop-blur border border-white/10 hover:bg-black/80 z-10"
        data-testid={`${testid}-prev`}
        aria-label="Previous"
      >
        <ChevronLeft size={18} />
      </button>
      <button
        onClick={() => scroll(1)}
        className="hidden md:flex absolute -right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center bg-black/60 backdrop-blur border border-white/10 hover:bg-black/80 z-10"
        data-testid={`${testid}-next`}
        aria-label="Next"
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
}

export default function ArtistDetail() {
  const { slug } = useParams();
  const [artist, setArtist] = useState(null);
  const [lightbox, setLightbox] = useState(null); // image url or null

  useEffect(() => {
    api.get(`/artists/${slug}`).then((r) => setArtist(r.data)).catch(() => setArtist(false));
  }, [slug]);

  // Esc closes lightbox
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => e.key === "Escape" && setLightbox(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  if (artist === null) {
    return <div className="pt-40 flex justify-center"><VinylLoader size={80} label="Loading artist" /></div>;
  }
  if (artist === false) {
    return (
      <div className="pt-40 text-center" data-testid="artist-not-found">
        <p className="text-zinc-400">Artist not found.</p>
        <Link to="/artists" className="glass-btn ghost mt-6 inline-flex">Back to roster</Link>
      </div>
    );
  }

  const gallery = artist.gallery || [];
  const videos = artist.videos || [];
  const interviews = artist.interviews || [];
  const bioParas = (artist.bio || "").split(/\n\n+/).filter(Boolean);

  return (
    <PageTransition>
      <Seo
        title={artist.name}
        description={artist.tagline || (artist.bio || "").slice(0, 160) || `${artist.name} on Pulse Media — listen, watch, and follow.`}
        image={artist.image}
      />
      {/* Cinematic hero */}
      <section className="relative pt-32 pb-12" data-testid={`artist-detail-${artist.slug}`}>
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${artist.image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "blur(40px) brightness(0.4)",
              transform: "scale(1.2)",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#08080A]/70 via-[#08080A]/85 to-[#08080A]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <Link to="/artists" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm mb-8" data-testid="artist-back">
            <ArrowLeft size={14} /> Back to roster
          </Link>

          <div className="grid md:grid-cols-5 gap-10 items-end">
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
              className="md:col-span-2"
            >
              <div className="rounded-3xl overflow-hidden border border-white/10 lift" style={{ aspectRatio: "3/4" }}>
                <img src={artist.image} alt={artist.name} className="w-full h-full object-cover" data-testid="artist-main-photo" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.7 }}
              className="md:col-span-3"
            >
              <div className="badge mb-5">{artist.tagline}</div>
              <h1 className="font-display text-6xl md:text-8xl font-bold tracking-tighter leading-[0.95]" data-testid="artist-name">
                {artist.name}
              </h1>
              {bioParas.length > 0 && (() => {
                const MAX = 600;
                const first = bioParas[0];
                const trimmed = first.length > MAX
                  ? first.slice(0, MAX).replace(/\s+\S*$/, "") + "…"
                  : first;
                return (
                  <p className="mt-7 text-lg text-zinc-300 leading-relaxed max-w-xl" data-testid="artist-bio">
                    {trimmed}
                  </p>
                );
              })()}

              {/* Streaming */}
              <div className="mt-10">
                <div className="label-mono mb-3 text-zinc-500">/ Stream</div>
                <div className="flex flex-wrap gap-3">
                  {artist.spotify && (
                    <a href={artist.spotify} target="_blank" rel="noopener noreferrer" className="glass-btn solid" data-testid="artist-spotify">
                      <Music size={14} /> Spotify <ExternalLink size={12} />
                    </a>
                  )}
                  {artist.apple_music && (
                    <a href={artist.apple_music} target="_blank" rel="noopener noreferrer" className="glass-btn" data-testid="artist-apple">
                      <Music size={14} /> Apple Music <ExternalLink size={12} />
                    </a>
                  )}
                  {artist.youtube && (
                    <a href={artist.youtube} target="_blank" rel="noopener noreferrer" className="glass-btn" data-testid="artist-youtube">
                      <Youtube size={14} /> YouTube <ExternalLink size={12} />
                    </a>
                  )}
                </div>
              </div>

              {/* Socials */}
              <div className="mt-7">
                <div className="label-mono mb-3 text-zinc-500">/ Follow</div>
                <div className="flex flex-wrap gap-2">
                  {artist.instagram && (
                    <a href={artist.instagram} target="_blank" rel="noopener noreferrer" className="glass-btn ghost p-3" aria-label="Instagram" data-testid="artist-instagram">
                      <Instagram size={16} />
                    </a>
                  )}
                  {artist.facebook && (
                    <a href={artist.facebook} target="_blank" rel="noopener noreferrer" className="glass-btn ghost p-3" aria-label="Facebook" data-testid="artist-facebook">
                      <Facebook size={16} />
                    </a>
                  )}
                  {artist.twitter && (
                    <a href={artist.twitter} target="_blank" rel="noopener noreferrer" className="glass-btn ghost p-3" aria-label="Twitter / X" data-testid="artist-twitter">
                      <Twitter size={16} />
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Music Videos */}
      {videos.length > 0 && (
        <Section className="px-6 lg:px-10 py-12 md:py-14 border-t border-white/5">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-8">
              <div>
                <div className="label-mono mb-3">/ Music Videos</div>
                <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter">Visuals.</h2>
              </div>
              <div className="text-zinc-500 text-sm">{videos.length} videos</div>
            </div>

            <Carousel testid="videos-carousel">
              {videos.map((yid) => (
                <div
                  key={yid}
                  data-carousel-item
                  className="snap-start shrink-0"
                  style={{ width: "min(440px, 80vw)" }}
                >
                  <YouTubeHoverCard youtubeId={yid} title="" category={artist.name} testid={`artist-video-${yid}`} />
                </div>
              ))}
            </Carousel>
          </div>
        </Section>
      )}

      {/* Photo Gallery */}
      {gallery.length > 0 && (
        <Section className="px-6 lg:px-10 py-12 md:py-14 border-t border-white/5">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-8">
              <div>
                <div className="label-mono mb-3">/ Gallery</div>
                <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter">Through the lens.</h2>
              </div>
              <div className="text-zinc-500 text-sm">{gallery.length} photos</div>
            </div>

            <Carousel testid="gallery-carousel">
              {gallery.map((src, i) => (
                <button
                  key={src}
                  data-carousel-item
                  onClick={() => setLightbox(src)}
                  className="snap-start relative shrink-0 rounded-2xl overflow-hidden border border-white/10 lift cursor-zoom-in"
                  style={{ width: "min(380px, 70vw)", aspectRatio: "3/4" }}
                  data-testid={`gallery-photo-${i + 1}`}
                >
                  <img src={src} alt={`${artist.name} photo ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                </button>
              ))}
            </Carousel>
          </div>
        </Section>
      )}

      {/* Interviews */}
      {interviews.length > 0 && (
        <Section className="px-6 lg:px-10 py-12 md:py-14 border-t border-white/5">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-8">
              <div>
                <div className="label-mono mb-3 inline-flex items-center gap-2">
                  <Mic2 size={12} className="text-cyan-400" /> / Press &amp; Interviews
                </div>
                <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter">In conversation.</h2>
              </div>
              <div className="text-zinc-500 text-sm">{interviews.length} interviews</div>
            </div>

            <Carousel testid="interviews-carousel">
              {interviews.map((yid) => (
                <div
                  key={yid}
                  data-carousel-item
                  className="snap-start shrink-0"
                  style={{ width: "min(440px, 80vw)" }}
                >
                  <YouTubeHoverCard youtubeId={yid} title="" category="Interview" testid={`artist-interview-${yid}`} />
                </div>
              ))}
            </Carousel>
          </div>
        </Section>
      )}

      {/* Booking CTA */}
      <Section className="px-6 lg:px-10 py-12 md:py-14 border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <div className="label-mono mb-4">/ Booking &amp; Press</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter">Work with {artist.name}.</h2>
          <p className="mt-5 text-zinc-400">For booking, features, and press inquiries.</p>
          <a href="mailto:contact@pulsemedia.vip" className="glass-btn solid mt-8 inline-flex" data-testid="artist-booking-email">
            contact@pulsemedia.vip <ExternalLink size={12} />
          </a>
        </div>
      </Section>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-10 cursor-zoom-out"
          style={{ background: "rgba(0,0,0,0.92)", backdropFilter: "blur(16px)" }}
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <img
            src={lightbox}
            alt=""
            className="max-w-full max-h-full object-contain rounded-2xl border border-white/10"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </PageTransition>
  );
}
